<template>
  <div class="login-step">
    <template v-if="view === 'choose'">
      <h2 class="login-title">Continue securely</h2>
      <p class="login-sub">Choose the quickest way to sign in.</p>

      <!-- One-click social sign-in -->
      <button
        type="button"
        class="button social-button is-fullwidth"
        :class="{ 'is-loading': loading === 'google' }"
        :disabled="!!loading"
        data-test="login-google"
        @click="googleLogin"
      >
        <span class="social-icon" aria-hidden="true">
          <svg viewBox="0 0 48 48">
            <path
              fill="#EA4335"
              d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
            />
            <path
              fill="#4285F4"
              d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
            />
            <path
              fill="#FBBC05"
              d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
            />
            <path
              fill="#34A853"
              d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
            />
          </svg>
        </span>
        <span>Continue with Google</span>
      </button>

      <button
        type="button"
        class="button social-button apple is-fullwidth"
        :class="{ 'is-loading': loading === 'apple' }"
        :disabled="!!loading"
        data-test="login-apple"
        @click="appleLogin"
      >
        <span class="social-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path
              d="M16.365 1.43c0 1.14-.417 2.2-1.11 2.98-.75.85-1.98 1.5-3.06 1.42-.13-1.09.42-2.24 1.09-2.98.74-.83 2.06-1.45 3.08-1.42zM20.5 17.2c-.55 1.27-.81 1.84-1.52 2.96-.99 1.57-2.39 3.52-4.12 3.53-1.54.02-1.94-1-4.03-.99-2.09.01-2.53 1.01-4.07.99-1.73-.02-3.06-1.78-4.05-3.35C-.02 15.9-.34 11.6 1.44 9.03c.99-1.44 2.55-2.28 4.02-2.28 1.5 0 2.44 1 3.68 1 1.2 0 1.93-1 3.67-1 1.31 0 2.7.71 3.69 1.94-3.24 1.78-2.71 6.4.99 8.51z"
            />
          </svg>
        </span>
        <span>Continue with Apple</span>
      </button>

      <div class="login-divider"><span>or</span></div>

      <!-- Passwordless email. A sign-in link both signs an existing customer
           in and creates an account for a new one. -->
      <div class="field">
        <label class="label" for="login-email">Email address</label>
        <div class="control">
          <input
            id="login-email"
            v-model="email"
            type="email"
            inputmode="email"
            autocomplete="email"
            placeholder="name@example.com"
            class="input"
            data-test="login-email-input"
            @input="clearMessage"
            @keyup.enter="sendLink"
          />
        </div>
      </div>

      <!-- Inline rather than a toast: a toast is gone before it has been read,
           and this is where the customer has to act on what went wrong. -->
      <div
        v-if="message"
        class="form-message"
        :class="messageType"
        role="alert"
        data-test="login-message"
      >
        {{ message }}
      </div>

      <button
        type="button"
        class="button ps-button is-fullwidth login-submit"
        :class="{ 'is-loading': loading === 'magic' }"
        :disabled="!!loading"
        data-test="login-magic-send"
        @click="sendLink"
      >
        Email me a secure sign-in link
      </button>

      <p class="login-help">
        No password needed. Works for new and existing customers.
      </p>

      <p class="by-clicking">
        By continuing, you accept the Pedalsure
        <a
          target="_blank"
          rel="noopener"
          href="/documents/november_2026/Terms_&_Conditions_11-2026.pdf"
        >
          Terms of Use
        </a>
        and acknowledge our
        <a
          target="_blank"
          rel="noopener"
          href="/documents/november_2026/Privacy_Policy_11-2026.pdf"
          >Privacy Policy</a
        >.
      </p>
    </template>

    <!-- Link sent: everything else drops away and only the next step shows. -->
    <div v-else class="inbox" data-test="login-inbox">
      <span class="inbox-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24">
          <rect
            x="3"
            y="5.5"
            width="18"
            height="13"
            rx="2.5"
            fill="none"
            stroke="currentColor"
            stroke-width="1.7"
          />
          <path
            d="M4 7.5l8 5.5 8-5.5"
            fill="none"
            stroke="currentColor"
            stroke-width="1.7"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </span>

      <h2 class="inbox-title">Check your inbox</h2>
      <p class="inbox-copy">
        We've sent a sign-in link to <strong>{{ sentTo }}</strong
        >. Open it on this device and you'll be signed straight in.
      </p>

      <a
        v-if="webmailUrl"
        class="button ps-button is-fullwidth"
        :href="webmailUrl"
        target="_blank"
        rel="noopener"
        data-test="login-open-mail"
      >
        Open email app
      </a>

      <div
        v-if="message"
        class="form-message"
        :class="messageType"
        role="status"
        data-test="login-inbox-message"
      >
        {{ message }}
      </div>

      <p class="inbox-retry">
        Didn't get it?
        <a data-test="login-resend" @click.prevent="resend">Send again</a>
        or
        <a data-test="login-change-email" @click.prevent="useAnotherEmail"
          >use another email</a
        >.
      </p>
    </div>
  </div>
</template>

<script>
/* eslint-disable no-console */
import { mapActions } from 'vuex'
import { auth, googleProvider, appleProvider } from '~/services/firebase'
import { sendMagicLink } from '~/services/emailLink'
import { setAuthContext, clearAuthContext } from '~/services/authContext'

// The /login counterpart of the quick quote's RegisterStep: same look, same
// three routes in (Google, Apple, emailed sign-in link), but it signs the
// customer in and sends them on to `redirectUrl` rather than saving a quote.

// Domains whose webmail we can open directly. Anything else gets no button —
// there is no reliable way to launch an unknown mail client, and a button that
// does nothing is worse than no button.
const WEBMAIL = {
  'gmail.com': 'https://mail.google.com',
  'googlemail.com': 'https://mail.google.com',
  'outlook.com': 'https://outlook.live.com/mail',
  'hotmail.com': 'https://outlook.live.com/mail',
  'hotmail.co.uk': 'https://outlook.live.com/mail',
  'live.com': 'https://outlook.live.com/mail',
  'live.co.uk': 'https://outlook.live.com/mail',
  'msn.com': 'https://outlook.live.com/mail',
  'yahoo.com': 'https://mail.yahoo.com',
  'yahoo.co.uk': 'https://mail.yahoo.com',
  'ymail.com': 'https://mail.yahoo.com',
  'icloud.com': 'https://www.icloud.com/mail',
  'me.com': 'https://www.icloud.com/mail',
  'mac.com': 'https://www.icloud.com/mail',
  'aol.com': 'https://mail.aol.com',
  'aol.co.uk': 'https://mail.aol.com',
  'protonmail.com': 'https://mail.proton.me',
  'proton.me': 'https://mail.proton.me',
}

export default {
  name: 'LoginStep',
  props: {
    redirectUrl: {
      type: String,
      default: '/dashboard',
    },
  },
  data() {
    return {
      view: 'choose', // choose | sent
      email: '',
      sentTo: '',
      loading: '',
      message: '',
      messageType: 'error', // error | info
    }
  },
  computed: {
    webmailUrl() {
      const domain = (this.sentTo.split('@')[1] || '').toLowerCase()
      return WEBMAIL[domain] || ''
    },
  },
  methods: {
    ...mapActions('user', ['login']),

    setMessage(type, text) {
      this.messageType = type
      this.message = text
    },
    clearMessage() {
      this.message = ''
    },

    validEmail() {
      if (!/.+@.+\..+/.test(this.email.trim())) {
        this.setMessage(
          'error',
          'Please enter your email address so we can send your link.'
        )
        return false
      }
      return true
    },

    magicLinkContext() {
      return { mode: 'login', redirectUrl: this.redirectUrl }
    },

    async sendLink() {
      if (this.loading || !this.validEmail()) return
      this.clearMessage()
      this.loading = 'magic'
      const address = this.email.trim()
      try {
        await sendMagicLink(address, this.magicLinkContext())
        this.sentTo = address
        this.view = 'sent'
      } catch (err) {
        this.reportAuthError('Email link', 'send', err)
      } finally {
        this.loading = ''
      }
    },

    // Resend from the confirmation screen. The address is fixed at this point,
    // so confirm in place rather than bouncing back to the form.
    async resend() {
      if (this.loading) return
      this.clearMessage()
      this.loading = 'magic'
      try {
        await sendMagicLink(this.sentTo, this.magicLinkContext())
        this.setMessage('info', `Sent again to ${this.sentTo}.`)
      } catch (err) {
        this.reportAuthError('Email link', 'resend', err)
      } finally {
        this.loading = ''
      }
    },

    useAnotherEmail() {
      this.view = 'choose'
      this.clearMessage()
      this.$nextTick(() => {
        const input = document.getElementById('login-email')
        if (input) input.focus()
      })
    },

    // Shared post-auth path: establish the session, then continue to wherever
    // the customer was headed. `nextUrl` has done its job by now, so it is
    // dropped from the query that is carried across.
    async afterAuth(data, provider) {
      await this.login({ data })
      clearAuthContext()
      this.$gtm.push({ event: 'login', provider })
      // eslint-disable-next-line no-unused-vars
      const { nextUrl, ...query } = this.$route.query
      await this.$router.push({ path: this.redirectUrl, query })
    },

    // Log the real Firebase code/message so failures stay debuggable, and show
    // the customer plain English instead of `auth/…`.
    reportAuthError(label, stage, err) {
      const code = (err && err.code) || ''
      const message = (err && err.message) || String(err)
      console.error(`[LoginStep] ${label} ${stage} error`, {
        code,
        message,
        err,
      })
      this.setMessage('error', this.friendlyError(code, label))
    },

    // Firebase codes → something a customer can act on. `label` names the
    // route they took ("Google", "Email link") for the generic fallback.
    friendlyError(code, label = '') {
      switch (code) {
        case 'auth/invalid-email':
          return 'That email address doesn’t look right. Please check it and try again.'
        case 'auth/too-many-requests':
          return 'Too many attempts. Please wait a few minutes and try again.'
        case 'auth/user-disabled':
          return 'This account has been disabled. Please get in touch and we’ll help.'
        case 'auth/network-request-failed':
          return 'We couldn’t reach Pedalsure. Check your connection and try again.'
        case 'auth/popup-closed-by-user':
        case 'auth/cancelled-popup-request':
          return 'Sign-in was cancelled. Please try again.'
        case 'auth/popup-blocked':
          return 'Your browser blocked the sign-in window. Allow pop-ups, or use a sign-in link instead.'
        case 'auth/account-exists-with-different-credential':
          return 'You already have an account with this email using a different sign-in method. Try the other button, or use a sign-in link.'
        default:
          return label
            ? `${label} didn’t work. Please try again, or use another option.`
            : 'Something went wrong. Please try again.'
      }
    },

    async socialLogin(label, loadingKey, provider) {
      if (this.loading) return
      this.loading = loadingKey
      this.clearMessage()
      // Persist the destination before handing off to the provider — Firebase
      // opens its OAuth handler in a new tab on mobile and this one may not
      // survive.
      setAuthContext({ mode: 'login', redirectUrl: this.redirectUrl })
      let data
      try {
        data = await auth.signInWithPopup(provider)
      } catch (err) {
        this.reportAuthError(label, 'sign-in', err)
        this.loading = ''
        return
      }
      try {
        await this.afterAuth(data, loadingKey)
      } catch (err) {
        // Auth succeeded but establishing the session failed.
        this.reportAuthError(label, 'post-sign-in', err)
      } finally {
        this.loading = ''
      }
    },
    googleLogin() {
      return this.socialLogin('Google', 'google', googleProvider)
    },
    appleLogin() {
      return this.socialLogin('Apple', 'apple', appleProvider)
    },
  },
}
</script>

<style lang="scss" scoped>
.login-step {
  text-align: left;
}

// --- Heading ------------------------------------------------------------
.login-title {
  font-size: $ps-text-h2;
  font-weight: 700;
  color: $ps-ink;
  line-height: 1.15;
}
.login-sub {
  color: $ps-body-color;
  font-size: $ps-text-small;
  margin: 8px 0 24px;
}

// --- Social -------------------------------------------------------------
.social-button {
  position: relative;
  height: $ps-btn-h;
  justify-content: center;
  font-weight: 600;
  margin-bottom: 12px;
  border: 1px solid $ps-border-color;
  border-radius: $ps-radius-md;

  .social-icon {
    position: absolute;
    left: 20px;
    width: 22px;
    height: 22px;
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      width: 22px;
      height: 22px;
    }
  }

  &.apple {
    background: $ps-ink;
    color: $ps-white;
    border-color: $ps-ink;

    &:hover {
      background: #000;
      color: $ps-white;
    }
  }

  &:disabled {
    opacity: 0.55;
  }
}
.login-divider {
  display: flex;
  align-items: center;
  text-align: center;
  color: $ps-muted;
  font-size: $ps-text-label;
  margin: 20px 0;

  &::before,
  &::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid $ps-border-color;
  }

  span {
    padding: 0 12px;
  }
}

// --- Email --------------------------------------------------------------
.field {
  margin-bottom: 16px;

  // The global form label is an uppercase, letter-spaced eyebrow. Here it sits
  // directly above a single field in a short stack, where sentence case reads
  // as part of the sentence the step is making.
  .label {
    font-size: $ps-text-small;
    font-weight: 600;
    color: $ps-ink;
    margin-bottom: 8px;
    text-transform: none;
    letter-spacing: normal;
  }

  .input {
    height: $ps-control-h;
    border-radius: $ps-radius-md;
  }
}
.login-submit {
  height: $ps-btn-h-cta;
  border-radius: $ps-radius-md;
  font-weight: 600;
}
.login-help {
  margin-top: 12px;
  text-align: center;
  font-size: $ps-text-label;
  color: $ps-muted;
  line-height: 1.4;
}

// --- Legal --------------------------------------------------------------
.by-clicking {
  padding: 24px 0 0;
  font-size: $ps-text-label;
  color: $ps-muted;
  line-height: 1.4;

  a {
    color: $ps-primary;
  }
}

// --- Feedback -----------------------------------------------------------
.form-message {
  border-radius: $ps-radius-sm;
  padding: 12px 14px;
  margin-bottom: 14px;
  font-size: 15px;
  line-height: 1.35;

  &.error {
    background: $ps-error-50;
    color: $ps-error;
  }

  &.info {
    background: $ps-surface;
    color: $ps-body-color;
  }
}

// --- Confirmation -------------------------------------------------------
.inbox {
  text-align: center;
  padding: 24px 0 8px;
}
.inbox-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  margin-bottom: 24px;
  border-radius: 50%;
  background: $ps-primary-50;
  color: $ps-primary;

  svg {
    width: 30px;
    height: 30px;
  }
}
.inbox-title {
  font-size: $ps-text-h2;
  font-weight: 700;
  color: $ps-ink;
  line-height: 1.15;
}
.inbox-copy {
  margin: 8px 0 24px;
  font-size: $ps-text-small;
  color: $ps-body-color;
  line-height: 1.5;
}
.inbox-retry {
  margin-top: 18px;
  font-size: $ps-text-small;
  color: $ps-body-color;

  a {
    color: $ps-primary;
    font-weight: 600;
    cursor: pointer;
  }
}
</style>
